/**
 * Graph colour palettes organized by category
 *
 * Each graph category has a distinct colour family to help users
 * quickly identify and distinguish between different types of data.
 */

export type GraphCategory = "battery" | "generation" | "load" | "fuel" | "hydrogen" | "default"

/**
 * Reserved for overlay lines only (SoC %, site load kW). Do not use for primary series or bar fills.
 * Dark rose — distinct from peak purple and from generation/load/fuel palettes.
 */
export const OVERLAY_LINE_COLOR = "#9d174d"

/**
 * Battery graph colours — discharge (red) vs charge (green) for clear contrast.
 * SoC line colour is always OVERLAY_LINE_COLOR in charts; `soc` mirrors that for legends/tooltips.
 */
export const BATTERY_COLORS = {
  // Red family for discharge
  discharge: ["#DC2626", "#B91C1C", "#991B1B", "#7F1D1D", "#450A0A"],
  // Green family for charge
  charge: ["#22C55E", "#16A34A", "#15803D", "#166534", "#14532D"],
  /** Per-series breakdown (same as discharge family; required by getCategoryColor) */
  stacked: ["#DC2626", "#B91C1C", "#991B1B", "#7F1D1D", "#450A0A"],
  soc: OVERLAY_LINE_COLOR,
  aggregated: "#DC2626",
}

/**
 * Generation graph colours
 * - Solar: yellow, Generator: purple
 * - Overall site power line: dark slate for contrast
 */
export const GENERATION_COLORS = {
  stacked: ["#EAB308", "#8B5CF6", "#22c55e", "#16a34a", "#15803d"],
  aggregated: "#22c55e", // Mid-green for aggregated view
  overallSitePower: "#334155", // Dark slate for total line
  overallSitePowerPeak: "#9CA3AF", // Grey for peak (consistent with other peaks)
  secondary: "#10b981", // Emerald for secondary metrics (e.g. fuel consumed)
}

/**
 * Map a generation asset to a specific colour based on its class or name/label.
 * Uses the asset class from the API when available, falls back to name matching.
 * Returns undefined when no match is found, so the caller can fall back to index-based colouring.
 */
export function getGenerationAssetColor(assetClass: string | undefined | null, nameOrLabel?: string | undefined | null): string | undefined {
  // Prefer class-based matching
  if (assetClass) {
    const lower = assetClass.toLowerCase()
    if (lower === "solar") return "#EAB308"   // yellow
    if (lower === "generator" || lower === "genset" || lower === "diesel") return "#8B5CF6" // purple
    if (lower === "battery" || lower === "bess") return "#22C55E" // green
  }
  // Fall back to name-based matching
  if (nameOrLabel) {
    const lower = nameOrLabel.toLowerCase()
    if (lower.includes("solar")) return "#EAB308"
    if (lower.includes("generator") || lower.includes("genset") || lower.includes("diesel")) return "#8B5CF6"
    if (lower.includes("battery") || lower.includes("bess")) return "#22C55E"
  }
  return undefined
}

/**
 * Load graph colours (amber/orange family)
 * - 10 hues for stacked bars to support systems with many connections
 * - Enhanced contrast for better visual distinction
 */
export const LOAD_COLORS = {
  stacked: [
    "#fbbf24", // amber-400
    "#f97316", // orange-500
    "#ea580c", // orange-600
    "#c2410c", // orange-700
    "#92400e", // amber-800
    "#d97706", // amber-600
    "#b45309", // amber-700
    "#dc2626", // red-600
    "#a16207", // yellow-700
    "#78350f", // amber-900
  ],
  aggregated: "#f59e0b", // Mid-amber for aggregated view
}

/**
 * Fuel graph colours (brown spectrum)
 * - Matches SEMANTIC_COLORS.fuel so monitoring and reports agree
 * - Used for fuel level and fuel consumed graphs
 * - Secondary metric (fuel consumed on the fuel level graph): lighter tan
 */
export const FUEL_COLORS = {
  stacked: ["#8B5A2B", "#A9743F", "#6B4423", "#C08A55", "#4E2F14"],
  aggregated: "#8B5A2B",
  secondary: "#C08A55",
}

/**
 * Hydrogen storage graph colours (blue-teal spectrum)
 * - Distinct from fuel's green/emerald and battery's red/green
 * - Spans cyan → teal → dark teal for multi-asset differentiation
 */
export const HYDROGEN_COLORS = {
  stacked: ["#06b6d4", "#0891b2", "#0e7490", "#155e75", "#164e63"],
  aggregated: "#0891b2",
}

/**
 * Phase colours — L1 red, L2 yellow, L3 blue. Shared by the monitoring and
 * reporting phase-balance charts.
 */
export const PHASE_COLORS = {
  l1: "#DC2626",
  l2: "#EAB308",
  l3: "#3B82F6",
}

/**
 * Semantic colours by what a series represents. Single source of truth for the
 * report graphs and icons; monitoring palettes above are unaffected.
 */
export const SEMANTIC_COLORS = {
  /** Site profile: power and energy */
  siteProfile: "#39aed2", // light blue
  fuel: "#8B5A2B", // brown
  generator: "#8B5CF6", // purple
  battery: "#22C55E", // green
  carbon: "#22C55E", // green
  neutral: "#64748B", // slate gray (e.g. clock / runtime icon)
}

/** Standard text colour for chart labels (legend entries, axis ticks, tooltips). */
export const LABEL_TEXT_COLOR = "#545454"

/**
 * Peak colour (consistent across all graph types) — grey
 */
export const PEAK_COLOR = "#9CA3AF"

/**
 * Default colours for graphs without a specific category
 */
export const DEFAULT_COLORS = {
  stacked: ["#39aed2", "#50c878", "#d2042d", "#f0986f", "#a170d9"],
  aggregated: "#39aed2",
}

/**
 * Get colour palette for a specific graph category
 */
export function getCategoryColors(category: GraphCategory) {
  switch (category) {
    case "battery":
      return BATTERY_COLORS
    case "generation":
      return GENERATION_COLORS
    case "load":
      return LOAD_COLORS
    case "fuel":
      return FUEL_COLORS
    case "hydrogen":
      return HYDROGEN_COLORS
    default:
      return DEFAULT_COLORS
  }
}

/**
 * Get a colour from the category's stacked palette by index
 */
export function getCategoryColor(category: GraphCategory, index: number): string {
  const colors = getCategoryColors(category)
  const stackedColors = colors.stacked
  return stackedColors[index % stackedColors.length]
}

/**
 * Get the aggregated line colour for a category
 */
export function getAggregatedColor(category: GraphCategory): string {
  return getCategoryColors(category).aggregated
}

/**
 * Derive graph category from filter properties
 */
export function deriveGraphCategory(filter?: {
  role?: string | null
  class?: string | null
  field?: string | null
}): GraphCategory {
  if (!filter) return "default"
  if (filter.field === "fuel_consumed") return "fuel"
  if (filter.class === "battery") return "battery"
  if (filter.class === "storage") return "hydrogen"
  if (filter.role === "generation") return "generation"
  if (filter.role === "consumer") return "load"
  if (filter.class === "fuel") return "fuel"
  return "default"
}
